import React from "react";
import styled from "styled-components";

import { AiOutlineSetting } from "react-icons/ai";
import { BsEye, BsPencilSquare } from "react-icons/bs";
import { BiAddToQueue, BiRefresh, BiTrash } from "react-icons/bi";

import useMobileSize from "features/shared/hooks/useMobileSize";
import SettingsModal from "./components/settingsModal";
import { useDispatch, useSelector } from "services/redux/rootReducer";

import { EditorAction } from "../../types/actions.editor";
import { ModalName } from "../../types/modal";
import { ImportExportType } from "../../types/importExport";

import SearchBar from "./components/searchBar";
import MenuButton from "./components/menuButton";
import ExpandableButton from "./components/importExportButton";

interface Props {
    onDeleteCallback: () => void;
    onRefreshCallback?: () => void;
    onExportCallback?: () => void;
    onModalPreloadCallback?: (id: string) => Promise<any>;
}

const MenuBar: React.FC<Props> = ({
    onDeleteCallback,
    onRefreshCallback,
    onExportCallback,
    onModalPreloadCallback,
}) => {
    const [isMobileSize] = useMobileSize();

    const dispatch = useDispatch();
    const { data, indexer, modal, menuConfig } = useSelector(
        (state) => state.editor
    );
    const { selectedItems } = useSelector((state) => state.table);

    const handleShowSettings = () => {
        dispatch({
            type: EditorAction.TOGGLE_MODAL,
            modal: {
                name: ModalName.SETTINGS,
            },
        });
    };

    const handleBasicAdd = async () => {
        dispatch({
            type: EditorAction.SET_IMPORT_TYPE,
            importType: ImportExportType.FORM,
        });
        dispatch({
            type: EditorAction.TOGGLE_MODAL,
            modal: {
                name: ModalName.ADD,
            },
        });
    };

    const handleViewOrUpdate = async (
        modalName: ModalName.VIEW | ModalName.UPDATE
    ) => {
        const selectedIndexer = selectedItems[selectedItems.length - 1]?.value;

        const latestSelectedItem = data.find(
            (item) => item[indexer] === selectedIndexer
        );

        dispatch({
            type: EditorAction.TOGGLE_MODAL,
            modal: {
                name: modalName,
                params: {
                    originalFields: onModalPreloadCallback
                        ? await onModalPreloadCallback(selectedIndexer)
                        : latestSelectedItem,
                },
            },
        });
    };

    return (
        <Container>
            <SearchBar />
            <ButtonContainer>
                <MenuButton
                    onClickHandler={handleShowSettings}
                    icon={AiOutlineSetting}
                />
                {!isMobileSize ? (
                    !menuConfig.disableImportExport ? (
                        <ExpandableButton
                            type="import"
                            startText="Add"
                            options={Object.values(ImportExportType)}
                        />
                    ) : (
                        <MenuButton
                            onClickHandler={handleBasicAdd}
                            icon={BiAddToQueue}
                            text="Add"
                        />
                    )
                ) : null}
                {onRefreshCallback &&
                !(isMobileSize && selectedItems.length === 1) ? (
                    <MenuButton
                        onClickHandler={onRefreshCallback}
                        icon={BiRefresh}
                        text="Refresh"
                    />
                ) : null}
                {selectedItems.length === 1 ? (
                    <>
                        <MenuButton
                            onClickHandler={() =>
                                handleViewOrUpdate(ModalName.VIEW)
                            }
                            icon={BsEye}
                            text="View"
                        />
                        {!isMobileSize ? (
                            <MenuButton
                                onClickHandler={() =>
                                    handleViewOrUpdate(ModalName.UPDATE)
                                }
                                icon={BsPencilSquare}
                                text="Edit"
                            />
                        ) : null}
                    </>
                ) : null}
                {selectedItems.length > 0 && !isMobileSize ? (
                    <>
                        <MenuButton
                            onClickHandler={onDeleteCallback}
                            icon={BiTrash}
                            text="Delete"
                        />
                        {onExportCallback && !menuConfig.disableImportExport ? (
                            <ExpandableButton
                                type="export"
                                startText="Export"
                                options={Object.values(ImportExportType).slice(
                                    1
                                )}
                                onClickHandler={onExportCallback}
                            />
                        ) : null}
                    </>
                ) : null}
            </ButtonContainer>
            {modal?.name === ModalName.SETTINGS && <SettingsModal />}
        </Container>
    );
};

const Container = styled.div`
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;

    overflow: hidden;
    user-select: none;

    & > * {
        display: flex;

        &:first-child {
            flex: 1;
            margin-left: 2.5%;
        }

        &:last-child {
            flex: 2; // to lock the width
        }
    }

    @media (max-width: 768px) {
        width: 100vw;

        & > * {
            &:first-child {
                margin-left: 1rem;
            }
        }
    }
`;

const ButtonContainer = styled.div`
    flex-flow: row-reverse;
    align-items: center;

    & > * {
        margin-right: 3%;
        &:first-child {
            margin-right: 2rem;
        }
    }
`;

export default MenuBar;
